/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import SectionTitle from "./SectionTitle";
import DetailItem from "./DetailItem";

const sumBy = (items, key) => {
    if (!Array.isArray(items)) return 0;
    return items.reduce((acc, cur) => acc + Number(cur?.[key] || 0), 0);
};

// Works across all discount sources despite `totalPrice` being overloaded:
// - campaign / IATA / admin-edit bake the discount into `totalPrice` and keep the
//   pre-discount figure in `totalPriceBeforeDiscount`;
// - quotation bookings keep the pre-discount figure in `totalPrice` (no
//   `totalPriceBeforeDiscount`) and the charged figure in `discountedPrice`.
// Anchoring on the before-discount price and subtracting `discount.amount` gives a
// correct before/after pair everywhere, so the two never collapse to the same value.
const getDiscountBreakdown = (booking) => {
    const totalPrice = Number(booking?.totalPrice || 0);
    const discount = booking?.discount || {};
    const discountAmount = Number(discount.amount || 0);
    const hasDiscount =
        discountAmount > 0 ||
        Boolean(discount.fromQuotation || discount.iataEligible || discount.campaignEligible);

    if (!hasDiscount) {
        return {
            hasDiscount: false,
            discountRate: 0,
            discountAmount: 0,
            discountedPrice: totalPrice,
            priceBeforeDiscount: totalPrice,
        };
    }

    const priceBeforeDiscount = Number(booking?.totalPriceBeforeDiscount || 0) || totalPrice;
    const discountedPrice = priceBeforeDiscount - discountAmount;

    return {
        hasDiscount: true,
        discountRate: Number(discount.rate || 0),
        discountAmount,
        discountedPrice,
        priceBeforeDiscount,
    };
};

const TimeCostDetail = ({
    booking
}) => {
    const bookingInfo = booking?.bookingInfo || [];
    const firstBookingInfo = bookingInfo[0] || {};
    const totalMeetAssist = sumBy(booking?.meetAssist || [], "meetAssistPrice");
    const discountBreakdown = getDiscountBreakdown(booking);
    const totalCost = discountBreakdown.discountedPrice;

    const formatDuration = (minutesValue) => {
        const totalMinutes = Number(minutesValue || 0);
        if (!totalMinutes) return "-";
        const rounded = Math.ceil((totalMinutes / 60) * 2) / 2;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${rounded} Hours (${hours}h${minutes > 0 ? `${minutes}m` : ""})`;
    };

    const formatMinutesToWords = (minutesValue) => {
        const totalMinutes = Math.floor(Number(minutesValue || 0));
        if (!totalMinutes) return "0";
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        if (hours > 0 && minutes === 0) return `${hours} ${hours > 1 ? "Hours" : "Hour"}`;
        if (hours > 0) return `${hours} ${hours > 1 ? "Hours" : "Hour"} ${minutes} ${minutes > 1 ? "Minutes" : "Minute"}`;
        return `${minutes} ${minutes > 1 ? "Minutes" : "Minute"}`;
    };

    const formatJPY = (value) => {
        const amount = Number(value || 0);
        if (!amount) return "-";
        return `${amount.toLocaleString()} JPY`;
    };

    return (
        <>
            <SectionTitle title="Time & Cost Details" />
            <div className="border rounded mb-5">
                <div className="d-flex align-items-start gap-5 p-3">
                    <div className="tw:grid tw:w-full tw:grid-cols-1 tw:gap-x-12 tw:gap-y-3 tw:md:grid-cols-5">
                        <DetailItem label="Total Time" value={formatDuration(booking?.billedDuration)} />
                        <DetailItem label="Total Driving Time" value={formatDuration(booking?.usageDuration)} />
                        <DetailItem label="Driver's Break Time" value={formatMinutesToWords(booking?.breakTime)} />
                        <DetailItem label="Waiting Time" value={formatMinutesToWords(booking?.additionalTime)} />
                        <DetailItem
                            label="Late-night Usage"
                            value={Number(booking?.nightHours || 0) > 0 ? formatMinutesToWords(Number(booking?.nightHours || 0) * 60) : "0"}
                        />
                    </div>
                </div>
                <hr className="hr-E8E8E8 m-0" />
                <div className="tw:p-3 tw:md:p-4">
                    {(bookingInfo.length ? bookingInfo : [firstBookingInfo]).map((vehicleInfo, index) => (
                        <div key={vehicleInfo?._id || vehicleInfo?.vehicleID?._id || `time-cost-vehicle-${index}`}>
                            <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-3 tw:md:gap-0">
                                <div className="tw:pr-2 tw:md:pr-4">
                                    <div className="tw:!font-hanken-grotesk tw:!m-0 tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">{`Vehicle #${index + 1}`}</div>
                                    <div className="tw:!font-hanken-grotesk tw:!mt-1 tw:!text-[15px] tw:!font-[400] tw:!text-[#282828]">
                                        {vehicleInfo?.vehicleTypeName || vehicleInfo?.vehicleTypeID?.typename || "-"}
                                    </div>
                                    <div className="tw:my-4 tw:h-px tw:w-full tw:bg-[#E8E8E8]" />
                                    <div className="tw:flex tw:flex-col tw:gap-2">
                                        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
                                            <div className="tw:!font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">Base Price</div>
                                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#282828]">{formatJPY(vehicleInfo?.baseprice)}</div>
                                        </div>
                                        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
                                            <div className="tw:!font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">30-min Extension Rate</div>
                                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#282828]">{formatJPY(vehicleInfo?.additional30MinRate || vehicleInfo?.extensionRate || vehicleInfo?.extensionPrice)}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="tw:border-t tw:border-[#E8E8E8] tw:px-2 tw:pt-4 tw:md:border-l tw:md:border-t-0 tw:md:px-4 tw:md:pt-0">
                                    <div className="tw:flex tw:flex-col tw:gap-2">
                                        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
                                            <div className="tw:!font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">Charter Cost</div>
                                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#282828]">{formatJPY(vehicleInfo?.vehiclePrice)}</div>
                                        </div>
                                        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
                                            <div className="tw:!font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">Toll Fee</div>
                                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#282828]">{formatJPY((vehicleInfo?.tollFare || 0) + (vehicleInfo?.travelAreaPrice || 0))}</div>
                                        </div>
                                        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
                                            <div className="tw:!font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">Late Night Fee</div>
                                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#8D8D8D]">{formatJPY(vehicleInfo?.nightPrice)}</div>
                                        </div>
                                        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
                                            <div className="tw:!font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">Long Distance Surcharge</div>
                                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#8D8D8D]">{formatJPY(vehicleInfo?.longDistanceSurcharge)}</div>
                                        </div>
                                        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
                                            <div className="tw:!font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">Meet & Assist Service</div>
                                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#282828]">{formatJPY(totalMeetAssist)}</div>
                                        </div>
                                        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
                                            <div className="tw:!font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">{`Add-on ${Number(vehicleInfo?.childSeatPrice || 0) > 0 ? "" : "(None)"}`}</div>
                                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#8D8D8D]">{formatJPY(vehicleInfo?.childSeatPrice)}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="tw:border-t tw:border-[#E8E8E8] tw:pl-2 tw:pt-4 tw:md:border-l tw:md:border-t-0 tw:md:pl-4 tw:md:pt-0">
                                    <div className="tw:flex tw:flex-col tw:gap-2">
                                        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
                                            <div className="tw:!font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">Discount Applicable?</div>
                                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#282828]">{discountBreakdown.hasDiscount ? "Yes" : "No"}</div>
                                        </div>
                                        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
                                            <div className="tw:!font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">Price Before Discount</div>
                                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#282828]">
                                                {formatJPY(discountBreakdown.priceBeforeDiscount)}
                                            </div>
                                        </div>
                                        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
                                            <div className="tw:!font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">Discount Rate</div>
                                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#8D8D8D]">{discountBreakdown.hasDiscount ? `${discountBreakdown.discountRate}%` : "-"}</div>
                                        </div>
                                        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
                                            <div className="tw:!font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">Discounted Amount</div>
                                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#8D8D8D]">{discountBreakdown.hasDiscount ? formatJPY(discountBreakdown.discountAmount) : "-"}</div>
                                        </div>
                                    </div>
                                    <div className="tw:my-4 tw:h-px tw:w-full tw:bg-[#E8E8E8]" />
                                    <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
                                        <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#282828]">Subtotal</div>
                                        <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[700] tw:!text-[#282828]">{formatJPY(totalCost)}</div>
                                    </div>
                                </div>
                            </div>
                            {index < (bookingInfo.length ? bookingInfo : [firstBookingInfo]).length - 1 ? <div className="tw:my-4 tw:h-px tw:w-full tw:bg-[#E8E8E8]" /> : null}
                        </div>
                    ))}
                </div>
                <hr className="hr-E8E8E8 m-0" />
                <div className="d-flex align-items-center justify-content-between p-3">
                    <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[700] tw:!text-[#282828]">Total</div>
                    <div className="tw:!font-hanken-grotesk tw:!text-[16px] tw:!font-[700] tw:!text-[#282828]">{formatJPY(totalCost)}</div>
                </div>
            </div>
        </>
    );
};

export default TimeCostDetail;
